import { DragOutlined } from '@ant-design/icons';
import FileUploadButton from '@components/common/FileUploadButton';
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FeedData, FeedDataItem } from '@models/feed';
import { cdnImageUrl, resizeImage } from '@utils/image';
import { getUniqueKeyFromUUID } from '@utils/string';
import { Button, Input, InputNumber, Switch } from 'antd';
import { RcFile } from 'antd/es/upload';
import React, { useEffect, useRef, useState } from 'react';
import DeeplinkActionEdit from './DeepLinkActionEdit';

const PREVIEW_WIDTH = 240; // 160 * 1.5
const PREVIEW_HEIGHT = 330; // 220 * 1.5
const DEFAULT_SWIPE_DELAY_SEC = 4;

const getBannerSortableId = (item: FeedDataItem, index: number) => `${item.imageUrl || 'empty'}_${index}`;

interface SortableBannerItemProps {
  onDeleteItem?: (event: any) => void;
  onChange?: (item: FeedDataItem) => void;
  onChangeImage?: (files: FileList) => void;
  id: any;
  item: FeedDataItem;
}

export const SortableBannerItem: React.FC<SortableBannerItemProps> = (props) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const { onDeleteItem, onChange, item } = props;

  return (
    <div ref={setNodeRef} style={{ ...style }}>
      <div style={{ display: 'flex', marginBottom: '8px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', width: PREVIEW_WIDTH, marginRight: '8px' }}>
          <div style={{ marginBottom: '4px' }}>
            <div
              style={{
                width: PREVIEW_WIDTH,
                marginRight: '12px',
                borderRadius: '8px',
                height: PREVIEW_HEIGHT,
                backgroundColor: 'white',
                position: 'relative',
                overflow: 'hidden',
                cursor: 'pointer',
              }}>
              <img
                style={{ width: PREVIEW_WIDTH, height: PREVIEW_HEIGHT, objectFit: 'cover' }}
                src={cdnImageUrl(item.imageUrlTemp || item.imageUrl)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: '30%',
                  left: 0,
                  right: 0,
                  marginLeft: '18px',
                  marginRight: '18px',
                }}>
                <div
                  style={{
                    color: 'white',
                    fontSize: '22px',
                    fontWeight: 700,
                    lineHeight: 1.4,
                    whiteSpace: 'pre-wrap',
                  }}>
                  {item.mainCopy1}
                  <br />
                  {item.mainCopy2}
                </div>
                <div
                  style={{
                    marginTop: '6px',
                    fontSize: '18px',
                    color: 'white',
                    opacity: 0.8,
                    lineHeight: 1.4,
                  }}>
                  {item.subCopy}
                </div>
              </div>
            </div>
          </div>
          <FileUploadButton
            accept={['jpg', 'jpeg', 'png', 'tif', 'tiff', 'webp', 'svg']
              .map((ext) => {
                return '.' + ext;
              })
              .join(',')}
            className="link-button"
            style={{
              fontSize: '14px',
              height: '32px',
              padding: '4px 15px',
              borderRadius: '6px',
              backgroundColor: '#ffffff',
              border: '1px solid #d9d9d9',
              color: 'rgba(0, 0, 0, 0.88)',
              boxShadow: '0 2px 0 rgba(0, 0, 0, 0.02)',
              cursor: 'pointer',
            }}
            onChange={(e) => {
              if (e.target.files) {
                const files = e.target.files;
                props.onChangeImage && props.onChangeImage(files);
              }
            }}>
            썸네일 변경
          </FileUploadButton>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginRight: '8px' }}>
          {/* <Checkbox
            checked={item.fixed}
            onChange={(e) => {
              onChange && onChange({ ...item, fixed: e.target.checked });
            }}>
            위치 고정
          </Checkbox> */}
          <Input
            value={item.mainCopy1}
            onChange={(e) => {
              onChange && onChange({ ...item, mainCopy1: e.target.value });
            }}></Input>
          <Input
            value={item.mainCopy2}
            onChange={(e) => {
              onChange && onChange({ ...item, mainCopy2: e.target.value });
            }}></Input>
          <Input
            value={item.subCopy}
            onChange={(e) => {
              onChange && onChange({ ...item, subCopy: e.target.value });
            }}></Input>
          <div style={{ border: '1px solid #eaeaea', borderRadius: '8px', padding: '8px' }}>
            <div>클릭시 이동 설정</div>
            <DeeplinkActionEdit
              data={item}
              onChange={(value) => {
                onChange && onChange({ ...item, ...value });
              }}></DeeplinkActionEdit>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Button
            onClick={(e) => {
              onDeleteItem && onDeleteItem(e);
            }}>
            삭제
          </Button>
          <div
            {...attributes}
            {...listeners}
            title="드래그해서 순서 변경"
            style={{
              cursor: isDragging ? 'grabbing' : 'grab',
              width: '100%',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #d9d9d9',
              borderRadius: '6px',
              backgroundColor: '#ffffff',
              color: '#999',
              fontSize: '16px',
              userSelect: 'none',
            }}>
            <DragOutlined />
          </div>
        </div>
      </div>
    </div>
  );
};

export type BannerEditHandle = {
  addEmptyBanner: () => void;
};

export const BannerEdit = React.forwardRef<
  BannerEditHandle,
  {
    clear?: boolean;
    data: Partial<FeedData<FeedDataItem>>;
    onChange?: (data: Partial<FeedData<FeedDataItem>>) => void;
    onChangeImageFile?: (files: { [key: string]: File }) => void;
  }
>((props, ref) => {
  const [changedContents, setChangedContents] = useState<FeedDataItem[]>();
  const [randomDisplay, setRandomDisplay] = useState<boolean>();
  const [autoplay, setAutoplay] = useState<boolean>();
  const [delay, setDelay] = useState<number>();
  const [imageFiles, setImageFiles] = useState<{ [key: string]: File }>({});
  const listEndRef = useRef<HTMLDivElement>(null);
  const shouldScrollToBottomRef = useRef(false);
  const { data } = props;
  const contents = changedContents || data.data || [];
  const isAutoplay = autoplay !== undefined ? autoplay : !!data.autoplay;
  const swipeDelay = delay !== undefined ? delay : data.delay ?? DEFAULT_SWIPE_DELAY_SEC;
  const sortableSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 10 },
    }),
  );

  const addEmptyBanner = () => {
    shouldScrollToBottomRef.current = true;
    setChangedContents((prev) => [...(prev ?? data.data ?? []), { type: 'banner', data: [] }]);
  };

  React.useImperativeHandle(ref, () => ({
    addEmptyBanner,
  }));

  useEffect(() => {
    if (!shouldScrollToBottomRef.current) {
      return;
    }
    shouldScrollToBottomRef.current = false;
    requestAnimationFrame(() => {
      listEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });
  }, [contents.length]);

  useEffect(() => {
    if (props.clear) {
      setChangedContents(undefined);
      setRandomDisplay(undefined);
      setAutoplay(undefined);
      setDelay(undefined);
      setImageFiles({});
    }
  }, [props.clear]);

  useEffect(() => {
    props.onChange &&
      props.onChange({
        ...data,
        data: changedContents || data.data,
        shuffle: randomDisplay !== undefined ? randomDisplay : data.shuffle,
        autoplay: autoplay !== undefined ? autoplay : data.autoplay,
        delay: delay !== undefined ? delay : data.delay,
      });
  }, [changedContents, randomDisplay, autoplay, delay]);

  useEffect(() => {
    props.onChangeImageFile && props.onChangeImageFile(imageFiles);
  }, [imageFiles]);

  const onSortEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      if (contents) {
        const oldIndex = contents.findIndex((item, index) => getBannerSortableId(item, index) === active.id);
        const newIndex = contents.findIndex((item, index) => getBannerSortableId(item, index) === over?.id);
        const newData = arrayMove(contents, oldIndex, newIndex);
        setChangedContents(newData);
      }
    }
  };

  const onChangeImage = async (files: FileList, index: number) => {
    const newFiles = await Promise.all(
      Array.from(files)
        .slice(0, 10)
        .map(async (file) => {
          const result = await resizeImage(file, { maxWidth: 960, maxHeight: 1320 });

          return result;
        }),
    );
    const contentTypes = newFiles[0].type.split('/');
    const uploadFileName = `${getUniqueKeyFromUUID()}.${contentTypes[1]}`;
    const newImageFile = new File([newFiles[0].image], uploadFileName) as RcFile;
    const tempUrl = URL.createObjectURL(newFiles[0].image);
    const previousImageUrl = contents[index]?.imageUrl;

    setChangedContents((prev) => {
      const newValues = [...(prev || contents)];
      newValues[index] = { ...newValues[index], imageUrl: uploadFileName, imageUrlTemp: tempUrl };
      return newValues;
    });

    setImageFiles((value) => {
      const newValue = { ...value };
      if (previousImageUrl && newValue[previousImageUrl]) {
        delete newValue[previousImageUrl];
      }
      newValue[uploadFileName] = newImageFile;
      return newValue;
    });
  };

  return (
    <div>
      <div style={{ display: 'flex', height: '44px', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>무작위 노출</span>
            <Switch
              checked={randomDisplay !== undefined ? randomDisplay : data.shuffle}
              onChange={(checked) => {
                setRandomDisplay(checked);
              }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>자동 스와이프</span>
            <Switch
              checked={isAutoplay}
              onChange={(checked) => {
                setAutoplay(checked);
                if (checked && delay === undefined && data.delay == null) {
                  setDelay(DEFAULT_SWIPE_DELAY_SEC);
                }
              }}
              checkedChildren="ON"
              unCheckedChildren="OFF"
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: isAutoplay ? undefined : '#bbb' }}>스와이프 간격(초)</span>
            <InputNumber
              min={1}
              max={60}
              value={swipeDelay}
              disabled={!isAutoplay}
              onChange={(value) => {
                setDelay(value ?? DEFAULT_SWIPE_DELAY_SEC);
              }}
              style={{ width: 80 }}
            />
          </div>
        </div>
      </div>
      <DndContext sensors={sortableSensors} collisionDetection={closestCenter} onDragEnd={onSortEnd}>
        <SortableContext
          items={contents.map((item, index) => {
            return { ...item, id: getBannerSortableId(item, index) };
          })}
          strategy={verticalListSortingStrategy}>
          <div style={{ maxWidth: '720px' }}>
            {contents.map((item, index) => {
              const sortableId = getBannerSortableId(item, index);
              return (
                <SortableBannerItem
                  key={sortableId}
                  item={item}
                  id={sortableId}
                  onDeleteItem={() => {
                    const deletedImageUrl = contents[index]?.imageUrl;
                    setChangedContents((prev) => {
                      const newValues = [...(prev || contents)];
                      newValues.splice(index, 1);
                      return newValues;
                    });
                    if (deletedImageUrl) {
                      setImageFiles((value) => {
                        if (!value[deletedImageUrl]) {
                          return value;
                        }
                        const newValue = { ...value };
                        delete newValue[deletedImageUrl];
                        return newValue;
                      });
                    }
                  }}
                  onChangeImage={(files: FileList) => {
                    onChangeImage(files, index);
                  }}
                  onChange={(item) => {
                    setChangedContents((prev) => {
                      const newValues = [...(prev || contents)];
                      newValues[index] = item;
                      return newValues;
                    });
                  }}></SortableBannerItem>
              );
            })}
            <div ref={listEndRef} />
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
});

BannerEdit.displayName = 'BannerEdit';

export default BannerEdit;
